import { Injectable, inject, Injector } from '@angular/core';
import {
  Firestore,
  serverTimestamp,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  updateDoc,
  deleteDoc,
  GeoPoint,
  query,
  where,
  orderBy,
  limit,
  DocumentSnapshot,
  DocumentReference,
  CollectionReference
} from 'firebase/firestore';
import { Auth } from 'firebase/auth';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap, from, defer, throwError, catchError, of, map } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from '../firebase-config';
import { SecureStorageService } from './secure-storage.service';
import { MenuController } from '@ionic/angular';
import { castObservable, typedCollectionRef, typedDocRef } from './firestore-typed-helpers';
import { docDataObservable, collectionDataObservable, docRefObservable } from './firestore-helpers';
import { FirestoreDocumentService } from './firestore-document.service';
import { buildCreateUserProfileData } from '../utils/user-profile-data.utils';
import {
  deriveRegistrationStateFromProfile,
  getPendingAgreementTypes,
  getPrimaryPendingAgreementType,
  resolveEffectiveRegistrationState,
} from '../utils/registration-state.utils';

import { BebopUser, RegistrationState } from 'src/app/interfaces/user';
import { Location } from 'src/app/interfaces/location';
import { Feedback } from 'src/app/interfaces/feedback';
import {
  cleanMobileInput,
  normalizeNlMobileToE164,
  normalizeMobileForRegistry,
  phoneRegistryDocIdFromE164,
} from '../utils/phone-registry.utils';
import {
  buildLocationAddress,
  findExactAddressMatch,
  type AddressGeocodeInput,
  type AddressGeocodeResult,
} from '../utils/address-utils';

type WorkerFiltersForm = Record<'jobs' | 'errands' | 'cleaning' | 'pets' | 'garden' | 'other', boolean>;
const isNgServe =
  typeof window !== 'undefined' && window.location.port === '4200';
const streetMapProvider = new OpenStreetMapProvider(
  isNgServe
    ? { searchUrl: '/nominatim/search', reverseUrl: '/nominatim/reverse' }
    : {}
);

function readStringField(source: unknown, field: string): string | undefined {
  if (typeof source !== 'object' || source === null) {
    return undefined;
  }

  const value = Reflect.get(source, field);
  return typeof value === 'string' ? value : undefined;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private auth = inject(FIREBASE_AUTH);
  private translate = inject(TranslateService);
  private firestore = inject(FIREBASE_FIRESTORE);
  private storage = inject(SecureStorageService);
  private menu = inject(MenuController);
  private injector = inject(Injector);
  private documentService = inject(FirestoreDocumentService);

  private readonly phoneRegistryCollection = 'phone_registry';
  private readonly registrationFlowVersion = 2;

  private readonly workerFilterOptions = ['jobs', 'errands', 'cleaning', 'pets', 'garden', 'other'] as const;

  private async waitForAuthInitialization(): Promise<void> {
    const authWithStateReady = this.auth as Auth & {
      authStateReady?: () => Promise<void>;
    };

    if (typeof authWithStateReady.authStateReady === 'function') {
      await authWithStateReady.authStateReady();
    }
  }

  private extractErrorCode(error: unknown): string | undefined {
    return readStringField(error, 'code');
  }

  private mapDocToUser(
    docSnap: DocumentSnapshot<BebopUser>
  ): BebopUser | undefined {
    if (!docSnap.exists()) {
      return undefined;
    }

    const data = docSnap.data();
    return data ? { uid: docSnap.id, ...data } : undefined;
  }

  private isFirestoreAuthError(error: unknown): boolean {
    const code = this.extractErrorCode(error);
    return code === 'permission-denied' || code === 'unauthenticated';
  }

  protected buildUserLocationDocRef = (userId: string, locationId: string) => {
    return this.documentService.buildUserLocationDocRef(userId, locationId);
  };

  protected buildUserDocRef = <T>(userId: string) => {
    return this.documentService.buildUserDocRef<T>(userId);
  };

  protected buildUserLocationsCollectionRef = (userId: string) => {
    return this.documentService.buildUserLocationsCollectionRef(userId);
  };

  protected buildNewLocationDocRef = (locationsRef: CollectionReference) => {
    return this.documentService.buildNewLocationDocRef(locationsRef);
  };

  protected buildCreateUserProfileData = (args: Parameters<typeof buildCreateUserProfileData>[0]) => {
    return buildCreateUserProfileData(args);
  };

  protected setDocument = (reference: DocumentReference, data: unknown): Promise<void> => {
    return this.documentService.setDocument(reference, data);
  };

  protected updateDocument = (reference: DocumentReference, data: unknown): Promise<void> => {
    return this.documentService.updateDocument(reference, data);
  };

  protected searchStreetAddress = async (searchString: string): Promise<AddressGeocodeResult[]> => {
    return (await streetMapProvider.search({
      query: searchString,
      addressdetails: 1,
      limit: 10,
    } as any)) as AddressGeocodeResult[];
  };

  async geocodeExactAddress(input: AddressGeocodeInput): Promise<{ latitude: string; longitude: string } | null> {
    const searchString = `${input.street} ${input.housenumber} ${input.city} Nederland`;
    const results = await this.searchStreetAddress(searchString);

    const exactMatch = findExactAddressMatch(results, input);

    if (!exactMatch) {
      return null;
    }

    return {
      latitude: String(exactMatch.y),
      longitude: String(exactMatch.x),
    };
  }

  async getUserId(): Promise<string> {
    await this.waitForAuthInitialization();

    // On native, use Firebase Auth directly; otherwise fallback to SecureStorage
    const firebaseUser = this.auth.currentUser;
    if (firebaseUser?.uid) {
      return firebaseUser.uid;
    }
    
    // Fallback: fetch from SecureStorage
    const userData = await this.storage.getObject<{ uid?: string }>('user');
    return userData?.uid || '';
  }

  async getUserEmail(): Promise<string | null> {
    const userData = await this.storage.getObject<{ email?: string | null }>('user');
    return userData?.email || null;
  }

  // checkForUserWithPhonenumber(mobile: string): AngularFirestoreCollection<BebopUser> {
  //   return this.fireStore.collection<BebopUser>(
  //     `/users`,
  //     ref =>
  //       ref
  //         .where('mobile', '==', mobile)
  //   );
  // }

  async createUser(form: {
    register_as?: string;
    firstname?: string;
    lastname?: string;
    gender?: string;
    birthdate?: string | Date;
    parental_approval?: boolean;
    business_name?: string;
    business_kvk?: string;
    referral_type?: string;
    referral_code?: string;
    referral_social?: string;
    referral_website?: string;
    mobile?: string;
    region_id?: string;
  }): Promise<void> {
    // console.log(form);
    const timestamp = serverTimestamp();
    const locationId = doc(collection(this.firestore, 'dummyCollection')).id;
    const userId = await this.getUserId();

    try {
      await this.createUserProfile(userId, form, locationId, timestamp);
      await this.createHomeLocation(userId, locationId);

      console.log('Profile and location created');
    } catch (error) {
      console.error('Error creating user:', error);
      throw error; // Re-throw the error if you want calling code to handle it
    }
  }

  async createUserProfile(userId: string, form: {
    register_as?: string;
    firstname?: string;
    lastname?: string;
    gender?: string;
    birthdate?: string | Date;
    parental_approval?: boolean;
    business_name?: string;
    business_kvk?: string;
    referral_type?: string;
    referral_code?: string;
    referral_social?: string;
    referral_website?: string;
    mobile?: string;
    region_id?: string;
  }, locationId: string, timestamp: ReturnType<typeof serverTimestamp>): Promise<void> {

    const is_client = form.register_as === 'client';
    const is_worker = form.register_as === 'worker';

    const dashboard = is_client ? 'client' : 'worker';

    const userRef = this.buildUserDocRef<BebopUser>(userId);

    const platform = Capacitor.getPlatform();
    const registrationSettings = await this.resolveRegistrationSettings();
    
    const userEmail = await this.getUserEmail();
    
    const cleanedMobile = cleanMobileInput(String(form.mobile ?? ''));
    const mobileE164 = normalizeNlMobileToE164(cleanedMobile);
    const mobileStored = mobileE164 || cleanedMobile;

    const userData = this.buildCreateUserProfileData({
      userId,
      form,
      locationId,
      timestamp,
      platform,
      userEmail,
      mobileStored,
      is_client,
      is_worker,
      dashboard,
      registrationSettings,
    });

    await this.setDocument(userRef, userData);
  }

  private async resolveRegistrationSettings(): Promise<{ darkmode: boolean; language: 'nl' | 'en' }> {
    const publicSettings = await this.storage.getObject<{ darkmode?: boolean; language?: string }>('public_settings');
    const currentLanguage = this.translate.getCurrentLang();
    const fallbackLanguage = this.translate.getFallbackLang();

    const activeLanguage: 'nl' | 'en' | undefined =
      currentLanguage === 'en' || currentLanguage === 'nl'
        ? currentLanguage
        : fallbackLanguage === 'en' || fallbackLanguage === 'nl'
          ? fallbackLanguage
          : undefined;

    // const darkmode = typeof publicSettings?.darkmode === 'boolean'
    //   ? publicSettings.darkmode
    //   : window.matchMedia('(prefers-color-scheme: dark)').matches;
    const language: 'nl' | 'en' =
      publicSettings?.language === 'en' || publicSettings?.language === 'nl'
        ? publicSettings.language
        : activeLanguage ?? 'nl';

    return { darkmode: false, language };
  }

  private buildMobileLookupCandidates(raw: string): string[] {
    const cleaned = cleanMobileInput(raw);
    if (!cleaned) {
      return [];
    }

    const candidates = new Set<string>();
    candidates.add(cleaned);

    const e164 = normalizeNlMobileToE164(cleaned);
    if (e164) {
      candidates.add(e164);
    }

    // Common NL variants: with/without leading 0.
    if (cleaned.startsWith('0') && cleaned.length > 1) {
      candidates.add(cleaned.slice(1));
    } else if (/^\d{9,10}$/.test(cleaned)) {
      candidates.add('0' + cleaned);
    }

    // +31 variants.
    if (cleaned.startsWith('+31') && cleaned.length > 3) {
      const rest = cleaned.slice(3);
      if (/^\d+$/.test(rest)) {
        candidates.add('0' + rest);
        candidates.add(rest);
      }
    }

    if (cleaned.startsWith('0031') && cleaned.length > 4) {
      const rest = cleaned.slice(4);
      if (/^\d+$/.test(rest)) {
        candidates.add('+31' + rest);
        candidates.add('0' + rest);
      }
    }

    return Array.from(candidates).slice(0, 10);
  }

  /**
   * Checks whether a phone number is already present in `phone_registry`.
   *
   * This avoids querying `/users` during registration, which is typically
   * blocked by Firestore security rules (only owners can read their profile).
   */
  async isMobileAlreadyUsed(rawMobile: string): Promise<boolean> {
    const e164 = normalizeMobileForRegistry(rawMobile);
    if (!e164) {
      return false;
    }

    const docId = phoneRegistryDocIdFromE164(e164);
    if (!docId) {
      return false;
    }

    const registryRef = doc(this.firestore, this.phoneRegistryCollection, docId);

    const currentUid = await this.getUserId();

    // Prefer server truth; fall back to cache if offline.
    try {
      const snap = await getDocFromServer(registryRef);
      if (!snap.exists()) {
        return false;
      }

      const existingUid = readStringField(snap.data(), 'uid');
      return !(existingUid && currentUid && existingUid === currentUid);
    } catch {
      const snap = await getDoc(registryRef);
      if (!snap.exists()) {
        return false;
      }

      const existingUid = readStringField(snap.data(), 'uid');
      return !(existingUid && currentUid && existingUid === currentUid);
    }
  }

  async setMobileVerified(mobile_verified: boolean): Promise<void> {
    await this.updateUserProfile({
      mobile_verified,
      registration_state: mobile_verified
        ? 'profile_pending'
        : 'phone_verification_pending',
      registration_state_updated_at: serverTimestamp(),
      registration_flow_version: this.registrationFlowVersion,
    });
  }

  private deriveRegistrationStateFromProfile(
    profile: BebopUser,
    audienceHint?: string
  ): RegistrationState {
    return deriveRegistrationStateFromProfile(profile, audienceHint);
  }

  getPendingAgreementTypes(profile: BebopUser, audienceHint?: string) {
    return getPendingAgreementTypes(profile, audienceHint);
  }

  getPrimaryPendingAgreementType(
    profile: BebopUser,
    audienceHint?: string
  ) {
    return getPrimaryPendingAgreementType(profile, audienceHint);
  }

  getEffectiveRegistrationState(
    profile: BebopUser,
    audienceHint?: string
  ): RegistrationState {
    return resolveEffectiveRegistrationState(profile, audienceHint);
  }

  async setRegistrationState(state: RegistrationState): Promise<void> {
    await this.updateUserProfile({
      registration_state: state,
      registration_state_updated_at: serverTimestamp(),
      registration_flow_version: this.registrationFlowVersion,
    });
  }

  async syncRegistrationState(audienceHint?: string): Promise<void> {
    const profile = await this.getUserProfileFromServer();
    if (!profile) {
      return;
    }

    const nextState = deriveRegistrationStateFromProfile(profile, audienceHint);
    await this.setRegistrationState(nextState);
  }

  async createHomeLocation(userId: string, locationId: string): Promise<void> {

    const locationRef = this.buildUserLocationDocRef(userId, locationId);
    const lang = this.translate.getCurrentLang();

    const locationData: Location = {
      id: locationId,
      title: '',
      is_home: true,
      street: '',
      housenumber: '',
      zipcode: '',
      city: '',
      geolocation: null,
      description: ''
    };

    await this.setDocument(locationRef, locationData);
  }

  private currentUserId: string | null = null;

  async setCurrentUserId(): Promise<void> {
    try {
      this.currentUserId = await this.getUserId();
    } catch (error) {
      console.warn('Failed to set current user ID:', error);
      this.currentUserId = null;
    }
  }

  private observeUserProfile(userDocRef: DocumentReference<BebopUser>): Observable<BebopUser | undefined> {
    const live$ = docRefObservable<BebopUser>(userDocRef, { idField: 'uid' });

    if (Capacitor.isNativePlatform()) {
      // Native WebViews can be finicky with long-lived streaming connections.
      // Prefer live updates, but fail gracefully by falling back to a one-time fetch.
      return live$.pipe(
        catchError((error) => {
          if (!this.isFirestoreAuthError(error)) {
            console.warn('Firestore user profile live listener error on native:', error);
          }

          return from(getDoc(userDocRef)).pipe(
            map((docSnap) => this.mapDocToUser(docSnap)),
            catchError((fallbackError) => {
              if (!this.isFirestoreAuthError(fallbackError)) {
                console.warn('Firestore getDoc fallback error on native:', fallbackError);
              }
              return of(undefined);
            })
          );
        })
      );
    }

    return live$.pipe(
      catchError((error) => {
        if (!this.isFirestoreAuthError(error)) {
          console.warn('Firestore getUserProfile live listener error:', error);
        }
        return of(undefined);
      })
    );
  }

  getUserProfile(): Observable<BebopUser | undefined> {
    // On native, use Firebase Auth directly; otherwise fallback to SecureStorage
    return defer(async () => {
      await this.waitForAuthInitialization();
      const firebaseUser = this.auth.currentUser;
      if (firebaseUser?.uid) {
        const userDocRef = this.buildUserDocRef<BebopUser>(firebaseUser.uid);
        return this.observeUserProfile(userDocRef);
      }
      
      // Fallback to current user ID if already set
      if (this.currentUserId) {
        const userDocRef = this.buildUserDocRef<BebopUser>(this.currentUserId);
        return this.observeUserProfile(userDocRef);
      } else {
        // Last resort: fetch user ID from SecureStorage
        return from(this.getUserId()).pipe(
          switchMap(userId => {
            if (!userId) {
                return of(undefined);
            }
            const userDocRef = this.buildUserDocRef<BebopUser>(userId);
            return this.observeUserProfile(userDocRef);
          })
        );
      }
    }).pipe(switchMap(stream => stream));
  }

  /**
   * Fetches the user profile from the server (bypassing cache where possible).
   * Useful right after backoffice approval toggles fields like `is_active`.
   */
  async getUserProfileFromServer(): Promise<BebopUser | undefined> {
    await this.waitForAuthInitialization();

    const uid = this.auth.currentUser?.uid || (await this.getUserId());
    if (!uid) {
      return undefined;
    }

    const userDocRef = this.buildUserDocRef<BebopUser>(uid);

    try {
      const docSnap = await getDocFromServer(userDocRef);
      return this.mapDocToUser(docSnap);
    } catch (error) {
      if (!this.isFirestoreAuthError(error)) {
        console.warn('Firestore getUserProfileFromServer error, falling back to getDoc:', error);
      }

      try {
        const docSnap = await getDoc(userDocRef);
        return this.mapDocToUser(docSnap);
      } catch (fallbackError) {
        if (!this.isFirestoreAuthError(fallbackError)) {
          console.warn('Firestore getDoc fallback error:', fallbackError);
        }
        return undefined;
      }
    }
  }

  getUserProfile$(): Observable<BebopUser | undefined> {
    return from(this.getUserProfile());
  }

  async getUserDashboard(): Promise<string | undefined> {
    const userId = await this.getUserId();
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);
    const userSnapshot = await getDoc(userDocRef);

    if (userSnapshot.exists()) {
      return userSnapshot.data()?.['dashboard'];
    } else {
      console.log('No such document!');
      return undefined;
    }
  }

  async editPublicProfile(_form: unknown, audience: string): Promise<void> {
    // console.log('editPublicProfile');
    // console.log(form);

    const userId = await this.getUserId();
    const client_profile_saved = audience === 'client';
    const worker_profile_saved = audience === 'worker';
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);

    await updateDoc(userDocRef, {
      registration_step: 3,
      registration_state: 'agreements_pending',
      registration_state_updated_at: serverTimestamp(),
      registration_flow_version: this.registrationFlowVersion,
      client_profile_saved,
      worker_profile_saved
    });
  }

  private async updateUserProfile(data: Partial<BebopUser>): Promise<void> {
    const userId = await this.getUserId();
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);
    await updateDoc(userDocRef, data);
  }

  async editClientProfile(form: { gender?: string; firstname?: string; lastname?: string }): Promise<void> {
    // console.log('editUserProfile');
    // console.log(form);

    await this.updateUserProfile({
      gender: form.gender,
      firstname: form.firstname,
      lastname: form.lastname
    });
  }

  async editClientSettings(form: { darkmode?: boolean; language?: string }): Promise<void> {
    // console.log('editUserSettings');
    // console.log(form);

    await this.updateUserProfile({
      setting_darkmode: form.darkmode,
      setting_language: form.language
    });
  }

  async editWorkerProfile(form: { gender?: string; firstname?: string; lastname?: string }): Promise<void> {
    // console.log('editUserProfile');
    // console.log(form);

    await this.updateUserProfile({
      gender: form.gender,
      firstname: form.firstname,
      lastname: form.lastname
    });
  }

  async editWorkerSettings(form: { darkmode?: boolean; language?: string }): Promise<void> {
    // console.log('editUserSettings');
    // console.log(form);

    await this.updateUserProfile({
      setting_darkmode: form.darkmode,
      setting_language: form.language
    });
  }

  async editWorkerFilters(form: WorkerFiltersForm): Promise<void> {
    const userId = await this.getUserId();
    const filters = this.workerFilterOptions
      .filter(option => form[option])
      .join(',');

    const userDocRef = this.buildUserDocRef<BebopUser>(userId);

    await updateDoc(userDocRef, { filters });
  }

  private async updateAgreement(agreementType: string, version: string): Promise<void> {
    const userId = await this.getUserId();
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);
    const timestamp = serverTimestamp();

    const updateData = {
      [`${agreementType}_agreed_to`]: version,
      [`${agreementType}_agreed_on`]: timestamp,
      registration_flow_version: this.registrationFlowVersion,
    };

    await updateDoc(userDocRef, updateData);
  }

  async agreeToTerms(version: string): Promise<void> {
    // console.log('version:', version);
    await this.updateAgreement('terms', version);
    await this.syncRegistrationState();
  }

  async agreeToPrivacy(version: string): Promise<void> {
    // console.log('version:', version);
    await this.updateAgreement('privacy', version);
    await this.syncRegistrationState();
  }

  async agreeToProvision(version: string): Promise<void> {
    // console.log('version:', version);
    await this.updateAgreement('provision', version);
    await this.syncRegistrationState();
  }

  async getUserCredits(): Promise<number> {
    const userId = await this.getUserId();
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);
    const userSnapshot = await getDoc(userDocRef);

    if (userSnapshot.exists()) {
      return userSnapshot.data()?.['credits'] ?? 0;
    } else {
      console.log('No such document!');
      return 0;
    }
  }

  async createLocation(form: { title?: string; street?: string; housenumber?: string; zipcode?: string; city?: string; description?: string, latitude: string, longitude: string }): Promise<void> {
    const userId = await this.getUserId();
    const locationsRef = this.buildUserLocationsCollectionRef(userId);
    const newLocationRef = this.buildNewLocationDocRef(locationsRef);
    const description = form.description ?? '';

    await this.setDocument(newLocationRef, {
      id: newLocationRef.id,
      title: form.title,
      street: form.street,
      housenumber: form.housenumber,
      zipcode: form.zipcode,
      city: form.city,
      geolocation: new GeoPoint(Number(form.latitude), Number(form.longitude)),
      description,
      is_home: false
    });
  }

  async editLocation(locationId: string, form: { title?: string; street?: string; housenumber?: string; zipcode?: string; city?: string; description?: string, latitude: string, longitude: string }): Promise<void> {
    const userId = await this.getUserId();
    const locationRef = this.buildUserLocationDocRef(userId, locationId);
    const description = form.description ?? '';

    const updateData: Record<string, unknown> = {
      street: form.street,
      housenumber: form.housenumber,
      zipcode: form.zipcode,
      city: form.city,
      geolocation: new GeoPoint(Number(form.latitude), Number(form.longitude)),
      description
    };

    // title is optional and must not be written as undefined (Firestore rejects it)
    if (form.title !== undefined) {
      updateData['title'] = form.title;
    }

    await this.updateDocument(locationRef, updateData);
  }

  getUserLocations(): Observable<Location[]> {
    return from(this.getUserId()).pipe(
      switchMap(userId => {
        const locationsRef = this.documentService.buildCollectionRef<Location>('users', userId, 'locations');
        const q = query(locationsRef, orderBy('is_home', 'desc'), orderBy('title'));
        return collectionDataObservable<Location>(this.firestore, () => q, { idField: 'id' });
      })
    );
  }

  async getLocationDetails(userId: string, locationId: string): Promise<Observable<Location | undefined>> {
    if (userId === '') {
        userId = await this.getUserId();
    }
    const locationRef = this.documentService.buildDocRef<Location>('users', userId, 'locations', locationId);
    return docRefObservable<Location>(locationRef);
  }

  async deleteLocation(locationId: string): Promise<void> {
    const userId = await this.getUserId();
    const locationRef = this.documentService.buildDocRef<Location>('users', userId, 'locations', locationId);
    await deleteDoc(locationRef);
  }

  async addNewRole(role: string): Promise<void> {
    const userId = await this.getUserId();
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);

    if (role === 'client') {
      await updateDoc(userDocRef, {
        is_client: true,
        client_profile_saved: true,
        dashboard: 'client',
      });
    } else if (role === 'worker') {
      await updateDoc(userDocRef, {
        is_worker: true,
        worker_profile_saved: true,
        dashboard: 'worker',
      });
    } else {
      throw new Error('Invalid role specified');
    }
  }

  async switchDashboard(newAudience: string, oldAudience: string) {
    this.storeDashboard(newAudience);

    this.menu.close().then(() => {
      // Lazy load Router via Injector to avoid circular dependency
      import('@angular/router').then(({ Router }) => {
        const router = this.injector.get(Router);
        router.navigate([newAudience + '/dashboard']);
      });
    });
  }  

  async storeDashboard(audience: string): Promise<void> {
    const userId = await this.getUserId();
    const userDocRef = this.buildUserDocRef<BebopUser>(userId);
    await updateDoc(userDocRef, {
      dashboard: audience,
    });
  }

  async getLocationDetailsForPublicOrder(userId: string = '', locationId: string): Promise<Location | undefined> {
    // console.log('locationId 2: ' + locationId);

    const locationRef = this.documentService.buildDocRef<Location>(`users/${userId}/locations/${locationId}`);
    const locationSnap: DocumentSnapshot<Location> = await getDoc(locationRef);

    return locationSnap.data();
  }

  getLocationsList(): Observable<Location[]> {
    return from(this.getUserId()).pipe(
      switchMap(userId => {
        const locationsRef = this.documentService.buildCollectionRef<Location>(`users/${userId}/locations`);
        const q = query(locationsRef, orderBy('is_home', 'desc'), orderBy('title'));
        return collectionDataObservable<Location>(this.firestore, () => q, { idField: 'id' });
      })
    );
  }

  async getRegionId(): Promise<string | undefined> {
    const userRef = this.buildUserDocRef<BebopUser>(await this.getUserId());
    const userProfileSnap = await getDoc(userRef);
    return userProfileSnap.data()?.['region_id'];
  }

  async checkLocationGeolocation(locationId: string): Promise<boolean | undefined> {
    const locationRef = this.documentService.buildDocRef<Location>(`users/${await this.getUserId()}/locations/${locationId}`);
    const locationSnap: DocumentSnapshot<Location> = await getDoc(locationRef);
    // console.log(locationSnap.data()?.['geolocation']);
    if (locationSnap.data()?.['geolocation']) {
      return true;
    }
    return false;
  }

  async saveNotificationSettings(form: { notification_setting?: number; notification_interval?: number }): Promise<void> {
    const userRef = this.buildUserDocRef<BebopUser>(await this.getUserId());
    await updateDoc(userRef, {
      notification_setting: form.notification_setting,
      notification_interval: form.notification_interval
    });
  }

  async saveNotificationToken(token: string): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) {
      console.warn('Skipping notification token save: missing user ID');
      return;
    }
    const userRef = this.buildUserDocRef<BebopUser>(userId);
    const platform = Capacitor.getPlatform();
    
    await updateDoc(userRef, {
      notification_id: token,
      device_platform: platform
    });
  }

  async saveAppState(state: string): Promise<void> {
    const userRef = this.buildUserDocRef<BebopUser>(await this.getUserId());
    await updateDoc(userRef, {
      app_state: state,
    });
  }

  async deleteUser(): Promise<void> {
    await this.waitForAuthInitialization();

    const userId = this.auth.currentUser?.uid;
    if (!userId) {
      throw new Error('Authenticated user required for account deletion.');
    }

    console.log('delete account: ' + userId);
    const userRef = this.buildUserDocRef<BebopUser>(userId);
    await this.updateDocument(userRef, {
      listener_action: 'delete-account',
      account_deleted_on: serverTimestamp(),
    });
  }

  getFeedbackList(audience: string): Observable<Feedback[]> {
    return from(this.getUserId()).pipe(
      switchMap(userId => {
        const feedbackRef = this.documentService.buildCollectionRef<Feedback>('feedback');
        const q = query(
          feedbackRef,
          where('user_id', '==', userId),
          where('audience', '==', audience),
          orderBy('created_at', 'desc')
        );
        return collectionDataObservable<Feedback>(this.firestore, () => q, { idField: 'id' });
      })
    );
  }

  async createFeedback(form: { title?: string; description?: string; category?: string }, audience: string): Promise<string> {
    const feedback_id = doc(collection(this.firestore, 'feedback')).id;
    const timestamp = serverTimestamp();
    const userId = await this.getUserId();

    const feedbackRef = this.documentService.buildDocRef<Feedback>(`feedback/${feedback_id}`);
    await setDoc(feedbackRef, {
      id: feedback_id,
      user_id: userId,
      audience: audience,
      title: form.title,
      description: form.description,
      category: form.category,
      status: 0,
      created_at: timestamp,
    });

    return feedback_id;
  }

  async createQuickFeedback(feedback: string, audience: string, pagePath: string, providedName?: string): Promise<string> {
    const feedbackId = doc(collection(this.firestore, 'feedback_friends')).id;
    const timestamp = serverTimestamp();
    const userId = await this.getUserId();

    let userFullname = providedName?.trim() ?? '';
    if (!userFullname) {
      try {
        const profile = await this.getUserProfileFromServer();
        const firstname = profile?.firstname?.trim() ?? '';
        const lastname = profile?.lastname?.trim() ?? '';
        userFullname = `${firstname} ${lastname}`.trim();
      } catch {
        userFullname = '';
      }
    }

    const feedbackRef = doc(this.firestore, `feedback_friends/${feedbackId}`);
    await setDoc(feedbackRef, {
      id: feedbackId,
      timestamp,
      user_id: userId,
      user_fullname: userFullname,
      feedback: feedback.trim(),
      audience,
      page_path: pagePath,
      created_at: timestamp,
    });

    return feedbackId;
  }

  getFeedbackDetails(feedbackId: string): Observable<Feedback | undefined> {
    const feedbackRef = this.documentService.buildDocRef<Feedback>(`feedback/${feedbackId}`);
    return docRefObservable<Feedback>(feedbackRef, { idField: 'id' });
  }
}
